import React, { useState } from 'react';
import styled from 'styled-components';
import ReactCrop, { centerCrop, Crop, makeAspectCrop, PercentCrop } from 'react-image-crop';
import isEmpty from 'lodash/isEmpty';
import Button, { ButtonVariations } from '../../SolvPatternLibrary/Button';
import { fontFamily, fontSize } from '../../../constants/text';
import Loading from '../../SolvPatternLibrary/Loading';
import { ReactCropStyles } from './CropImage';
import { UploadStatus } from '../../../constants/index';

const MAX_PHOTO_DIMENSION = 512;

const Root = styled.div.withConfig({ componentId: 'sc-wukkfo-0' })`
  margin: 32px auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 24px;
`;

const LoadingContainer = styled.div.withConfig({ componentId: 'sc-wukkfo-1' })`
  display: flex;
  max-width: 100%;
  text-align: center;
  margin: 20px auto;
  justify-content: center;
  min-height: 170px;
`;

const SaveButton = styled(Button).withConfig({ componentId: 'sc-wukkfo-2' })`
  font-size: ${fontSize.mediumSmall};
  font-family: ${fontFamily.bold};
`;

type Props = {
  handlePhotoSubmit?: (...args: any[]) => any;
  handleCancel?: (...args: any[]) => any;
  userProfile: any;
  userProfilePhoto?: any;
  profileColor?: string;
  src: string;
};

const ProfilePhotoEdit = ({
  handlePhotoSubmit,
  userProfile,
  userProfilePhoto,
  profileColor,
  src,
}: Props) => {
  const [crop, setCrop] = useState<Crop>();
  const [percentCrop, setPercentCrop] = useState<PercentCrop>();
  const [saveCropInProgress, setSaveCropInProgress] = useState(false);
  const [imageRef, setImageRef] = useState<HTMLImageElement | null>(null);

  const getCroppedImg = (image: HTMLImageElement, percentCrop: PercentCrop) => {
    const canvas = document.createElement('canvas');
    // Resize the photo down to 512 x 512 if it is larger.
    // This currently assumes we only allow square photos, as is accurate since the crop region
    // is constrained to a 1:1 aspect ratio
    // Resizing occurs by constraining the canvas dimensions to the crop region scaled down to
    // fit within 512 x 512, and then drawImage() renders the image at the scaled dimensions.
    canvas.width = Math.min(image.naturalWidth * (percentCrop.width / 100), MAX_PHOTO_DIMENSION);
    canvas.height = canvas.width;

    const ctx = canvas.getContext('2d');

    ctx?.drawImage(
      image as CanvasImageSource,
      image.naturalWidth * (percentCrop.x / 100),
      image.naturalHeight * (percentCrop.y / 100),
      image.naturalWidth * (percentCrop.width / 100),
      image.naturalHeight * (percentCrop.height / 100),
      0,
      0,
      canvas.width,
      canvas.height
    );

    return new Promise((resolve) => {
      const mimeString = 'image/jpeg';
      // TODO: ? export const DEFAULT_JPEG_QUALITY = 0.92;
      const newDataURL = canvas.toDataURL(mimeString);
      const byteString = atob(newDataURL.split(',')[1]);

      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; ++i) {
        ia[i] = byteString.charCodeAt(i);
      }

      const newBlob = new Blob([ab], { type: mimeString });
      resolve(newBlob);
    });
  };

  const handleSave = () => {
    if (!imageRef || !percentCrop) {
      return;
    }

    setSaveCropInProgress(true);
    getCroppedImg(imageRef, percentCrop).then(handlePhotoSubmit);
  };

  if (saveCropInProgress || userProfilePhoto.status === UploadStatus.PROCESSING) {
    return (
      <Root>
        <LoadingContainer>
          <Loading size={130} />
        </LoadingContainer>
      </Root>
    );
  }

  if (!isEmpty(src)) {
    return (
      <Root>
        <ReactCropStyles>
          <ReactCrop
            aspect={1}
            crop={crop}
            onChange={(crop, percentCrop) => {
              setCrop(crop);
              setPercentCrop(percentCrop);
            }}
            onComplete={(crop, percentCrop) => {
              setCrop(crop);
              setPercentCrop(percentCrop);
            }}
          >
            <img
              height="auto"
              onLoad={(e) => {
                setImageRef(e.currentTarget);
                setCrop(
                  centerCrop(
                    makeAspectCrop(
                      {
                        unit: '%',
                        width: 80,
                      },
                      1,
                      e.currentTarget.naturalWidth,
                      e.currentTarget.naturalHeight
                    ),
                    e.currentTarget.naturalWidth,
                    e.currentTarget.naturalHeight
                  )
                );
              }}
              src={src ?? ''}
              style={{
                maxHeight: '420px',
              }}
              width="auto"
            />
          </ReactCrop>
        </ReactCropStyles>
        <SaveButton
          disabled={!imageRef || !percentCrop}
          onClick={handleSave}
          variation={ButtonVariations.link}
        >
          SAVE
        </SaveButton>
      </Root>
    );
  }

  return null;
};

export default ProfilePhotoEdit;
